import React, { createContext, useCallback, useContext, useState } from 'react'

import type { TrackId } from 'types/track'

type SearchNestingContextValue = {
  expandedTrackIds: Set<TrackId>
  isExpanded: (_trackId: TrackId) => boolean
  toggleNesting: (_trackId: TrackId) => void
}

const SearchNestingContext = createContext<SearchNestingContextValue>({
  expandedTrackIds: new Set(),
  isExpanded: () => false,
  toggleNesting: () => {},
})

export const SearchNestingProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [expandedTrackIds, setExpandedTrackIds] = useState<Set<TrackId>>(
    () => new Set(),
  )

  const toggleNesting = useCallback((trackId: TrackId) => {
    setExpandedTrackIds(prev => {
      const next = new Set(prev)
      if (next.has(trackId)) {
        next.delete(trackId)
      } else {
        next.add(trackId)
      }
      return next
    })
  }, [])

  const isExpanded = useCallback(
    (trackId: TrackId) => expandedTrackIds.has(trackId),
    [expandedTrackIds],
  )

  return (
    <SearchNestingContext.Provider
      value={{ expandedTrackIds, isExpanded, toggleNesting }}
    >
      {children}
    </SearchNestingContext.Provider>
  )
}

export const useSearchNesting = () => useContext(SearchNestingContext)
